import React, { useEffect, useState } from 'react';
import Localize from 'react-intl-universal';

import { Field, useFormikContext } from 'formik';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { ADDRESS_OPTIONS } from '@common/Constants';
import { BOOKING_PROCESS } from '@common/network';
import AutocompleteComponent from '@components/AutocompleteComponent';
import SelectInput from '@components/SelectInput/SelectInput';
import TextInput from '@components/TextInput/TextInput';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import { participantsOptions, euCountriesIds } from '@pages/BookingProcess/Constants';

const StyledGrid = styled(Grid)(() => ({ '&.MuiGrid-root': { marginTop: '0.1rem' } }));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  background: theme.palette.booking.list
}));

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  '.MuiAccordionSummary-content.Mui-expanded ': { marginBottom: 0 }
}));

const StyledHeader = styled(Box)(({ theme }) => ({
  marginTop: '0.5rem',
  marginBottom: 0,
  paddingTop: 0,
  fontSize: '20px',
  color: theme.palette.booking.header
}));

const bookingPersonOptions = () => [
  { value: participantsOptions.participant, label: Localize.get('Booking.iAmParticipant'), id: 1 },
  {
    value: participantsOptions.bookingPerson,
    label: Localize.get('Booking.imBookingPerson'),
    id: 2
  }
];

const IS_LAYER = import.meta.env.VITE_CLIENT_BRAND === 'layher';

const EXPAND_SMALL = { generalInformationAccordion: true, contactInfoAccordion: true };
const EXPAND_LARGE = { generalInformationAccordion: true, contactInfoAccordion: true };

const CreateDialogForm = ({
  configuration = {},
  setIsFormValid,
  disableKeyFields = false,
  bookingPersonForm = true,
  isCreateNewParticipant = false,
  isEventFree = false,
  isEventIlias
}) => {
  const theme = useTheme();

  const { isValid, values, setFieldValue, setValues, setFieldTouched } = useFormikContext();
  const [contextData] = useGlobalContext();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(EXPAND_SMALL);
  const isBookingPerson = values?.isBookingPerson === participantsOptions?.bookingPerson;

  const {
    country: countryOptions,
    title: titleOptions,
    gender: genderOptions,
    nativelanguage: languageOptions,
    academicTitle: academicOptions,
    industrySectorOptions
  } = contextData?.bookingFormData || {};

  useEffect(() => {
    setIsFormValid(isValid);
  }, [isValid]);

  useEffect(() => {
    setExpanded(isSmallScreen ? EXPAND_SMALL : EXPAND_LARGE);
  }, [isSmallScreen]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded({ ...expanded, [panel]: isExpanded });
  };
  const configurationFieldVisibility =
    !configuration?.BookingProcess?.hideBookingFields && !isEventFree;

  return (
    <>
      <StyledAccordion
        expanded={expanded?.generalInformationAccordion}
        onChange={handleChange('generalInformationAccordion')}
      >
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='generalInformationAccordion-content'
          id='generalInformationAccordion-header'
        >
          <StyledHeader>{Localize.get('Booking.generalInfo')}</StyledHeader>
        </StyledAccordionSummary>
        <AccordionDetails>
          <StyledGrid container rowSpacing={{ xs: 2 }} columnSpacing={{ xs: 4, md: 16 }}>
            {bookingPersonForm && (
              <Grid item xs={12} sm={12} sx={{ mb: 1 }}>
                <RadioGroup
                  name={'isBookingPerson'}
                  value={values.isBookingPerson}
                  onChange={(event) => {
                    const { value, name } = event.target;
                    setFieldValue(name, value);
                  }}
                  row
                >
                  {bookingPersonOptions().map((option, index) => (
                    <FormControlLabel
                      key={index}
                      value={option.value}
                      control={<Radio size='small' />}
                      label={option.label}
                      disabled={!!values?.bookingPersonSelectDisabled}
                    />
                  ))}
                </RadioGroup>
              </Grid>
            )}

            {isEventIlias && !isBookingPerson && (
              <>
                <Grid item xs={12} sm={12} sx={{ mb: 1 }}>
                  <RadioGroup
                    name='hasIliasAccount'
                    value={values.hasIliasAccount ?? false}
                    onChange={(event) => {
                      const { value, name } = event.target;

                      const boolValue = value === 'true';

                      setFieldValue(name, boolValue);

                      if (!boolValue) {
                        setFieldValue('iliasUsername', '');
                      }
                    }}
                    row
                  >
                    <FormControlLabel
                      value={false}
                      control={<Radio size='small' />}
                      label={Localize.get('Booking.noIliasIntegration')}
                    />
                    <FormControlLabel
                      value={true}
                      control={<Radio size='small' />}
                      label={Localize.get('Booking.hasIliasAccount')}
                    />
                  </RadioGroup>
                </Grid>

                {values.hasIliasAccount && (
                  <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
                    <FormControl required fullWidth size='small'>
                      <Field name='iliasUsername' label='Username' component={TextInput} required />
                    </FormControl>
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'generalInformation.title'}
                  label={Localize.get('Booking.title')}
                  component={SelectInput}
                  options={titleOptions}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'generalInformation.academicTitle'}
                  label={Localize.get('Booking.academicTitle')}
                  component={SelectInput}
                  options={academicOptions}
                />
              </FormControl>
            </Grid>
            {!theme?.hideFields?.gender && (
              <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
                <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                  <Field
                    name={'generalInformation.gender'}
                    label={Localize.get('Booking.gender')}
                    component={SelectInput}
                    options={genderOptions}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'generalInformation.firstName'}
                  label={Localize.get('SignIn.firstName')}
                  component={TextInput}
                  disabled={disableKeyFields}
                  required
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'generalInformation.lastName'}
                  label={Localize.get('SignIn.lastName')}
                  component={TextInput}
                  disabled={disableKeyFields}
                  required
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'generalInformation.language'}
                  label={Localize.get('Booking.language')}
                  component={SelectInput}
                  options={languageOptions}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'generalInformation.company'}
                  label={Localize.get('Booking.company')}
                  component={TextInput}
                  required={IS_LAYER}
                />
              </FormControl>
            </Grid>
            {configurationFieldVisibility && (
              <>
                <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
                  <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                    <Field
                      name={'generalInformation.department'}
                      label={Localize.get('MyProfile.departmentName')}
                      component={TextInput}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
                  <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                    <Field
                      name={'generalInformation.functionField'}
                      label={Localize.get('Booking.functionField')}
                      component={TextInput}
                    />
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'generalInformation.reference'}
                  label={Localize.get('Booking.reference')}
                  component={TextInput}
                />
              </FormControl>
            </Grid>
            {bookingPersonForm &&
              configuration?.BookingProcess?.customerNumberFieldVisibilityClients?.includes(
                import.meta.env.VITE_CLIENT_BRAND
              ) && (
                <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
                  <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                    <Field
                      name={'generalInformation.customerNumber'}
                      label={Localize.get('Booking.customerNumber')}
                      component={TextInput}
                    />
                  </FormControl>
                </Grid>
              )}
            {bookingPersonForm &&
              configuration?.BookingProcess?.industrySectorFieldVisibilityClients?.includes(
                import.meta.env.VITE_CLIENT_BRAND
              ) && (
                <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
                  <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                    <Field
                      name={'generalInformation.industrySector'}
                      label={Localize.get('Booking.industrySector')}
                      component={SelectInput}
                      options={industrySectorOptions}
                    />
                  </FormControl>
                </Grid>
              )}
          </StyledGrid>
        </AccordionDetails>
      </StyledAccordion>

      <StyledAccordion
        expanded={expanded?.contactInfoAccordion}
        onChange={handleChange('contactInfoAccordion')}
      >
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='contactInfoAccordion-content'
          id='contactInfoAccordion-header'
        >
          <StyledHeader>{Localize.get('Booking.contactInfo')}</StyledHeader>
        </StyledAccordionSummary>
        <AccordionDetails>
          <StyledGrid container rowSpacing={{ xs: 2 }} columnSpacing={{ xs: 4, md: 16 }}>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'contactInformation.email'}
                  label={Localize.get('Booking.email')}
                  component={TextInput}
                  disabled={disableKeyFields}
                  required
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'contactInformation.phone'}
                  label={Localize.get('Booking.phone')}
                  component={TextInput}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'contactInformation.mobile'}
                  label={Localize.get('Booking.mobile')}
                  component={TextInput}
                  maxLength='20'
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'contactInformation.street'}
                  label={Localize.get('Booking.street')}
                  component={TextInput}
                  required={!isEventFree}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'contactInformation.number'}
                  label={Localize.get('Booking.streetNumber')}
                  component={TextInput}
                  required={!isEventFree}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'contactInformation.postCode'}
                  label={Localize.get('SignIn.postcode')}
                  component={TextInput}
                  required={!isEventFree}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'contactInformation.city'}
                  label={Localize.get('SignIn.city')}
                  component={TextInput}
                  required={!isEventFree}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'contactInformation.country'}
                  label={Localize.get('SignIn.country')}
                  component={AutocompleteComponent}
                  options={countryOptions}
                  formItemChangeHandler={(_, data) => {
                    setValues({
                      ...values,
                      contactInformation: {
                        ...values.contactInformation,
                        country: data?.newValue,
                        region: null
                      }
                    });
                    setFieldTouched && setFieldTouched('generalInformation.vatNo', true);
                  }}
                  required={!isEventFree}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={'contactInformation.region'}
                  label={Localize.get('Booking.region')}
                  component={AutocompleteComponent}
                  formItemChangeHandler={(_, data) =>
                    setFieldValue('contactInformation.region', data?.newValue)
                  }
                  path={BOOKING_PROCESS.GET_REGIONS}
                  pathVariables={{ id: values?.contactInformation?.country?.id }}
                  pathVariablesRequired={['id']}
                  temApi
                />
              </FormControl>
            </Grid>

            {!isCreateNewParticipant && (
              <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
                <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                  <Field
                    name={'generalInformation.vatNo'}
                    label={Localize.get('Booking.vatNo')}
                    component={TextInput}
                    required={
                      !isEventFree &&
                      !isCreateNewParticipant &&
                      euCountriesIds.includes(
                        (values?.billingAddressRadio === ADDRESS_OPTIONS.alternative
                          ? values?.billingAddress?.country?.id
                          : 0) ||
                          values?.contactInformation?.country?.id ||
                          0
                      )
                    }
                  />
                </FormControl>
              </Grid>
            )}
          </StyledGrid>
        </AccordionDetails>
      </StyledAccordion>
    </>
  );
};
export default CreateDialogForm;
